/*
 * @providesModule NetworkConnectivityClient
 * 
 */

import React from 'react';

import ClientPropertyConstants from 'ClientPropertyConstants';
import ModalNavigationController from 'ModalNavigationController';
import StaticWarningModal from 'StaticWarningModal';
import {_m} from 'INTL';
import Tournament from 'SKZTournament';
import LoadingIndicator from 'LoadingIndicator';
import SyncMatchMakingController from 'SyncMatchMakingController';
import {Navigator} from 'react-native-deprecated-custom-components';

import {
  NativeModules,
  Platform,
} from 'react-native';

const SkillzModule = NativeModules.SkillzModule;
const RNNetworkConnectivityManager = NativeModules.RNNetworkConnectivityManager;

class NetworkConnectivityClient {
  mostRecentPing: number;
  averagePing: number;
  isWifi: boolean;
  recentPings: number[];

  constructor(props) {
    this.recentPings = [];
    this.averagePing = -1;
    this.mostRecentPing = -1;
    this.isWifi = false;
  }

  checkConnection(tournament: Tournament, navigator: Navigator, completion: (success: boolean) => void, isRetry:boolean = false) {
    if (!isRetry) {
      LoadingIndicator.show();
    }
    const allowCellConnection = ClientPropertyConstants.getIsSyncCellEnabled();

    this._getPingOnce((pingResults: Object) => {
      this._hideLoading();
      const isWifi = this.isWifi;
      let wifiStrength = 0;
      let cellStrength = 0;
      let sufficientSignalStrength = false;

      if (isWifi) {
        wifiStrength = this._getWifiSignalStrength();
        sufficientSignalStrength = wifiStrength > 3;
      } else {
        cellStrength = allowCellConnection ? this._getCellSignalStrength() : 0;
        sufficientSignalStrength = allowCellConnection ? cellStrength > 2 : false;
      }

      if (!sufficientSignalStrength && !ClientPropertyConstants.getFastMatchV2Enabled()) {
        SyncMatchMakingController.setSearchingModalsEnabled(false);
        if (isWifi) {
          this._displayWifiSignalWarningModal(tournament, wifiStrength, navigator, completion);
        } else {
          this._displayCellSignalWarningModal(tournament, cellStrength, navigator, completion);
        }
      } else {
        SyncMatchMakingController.setSearchingModalsEnabled(true);
        completion(true);
      }
    });
  }

  _getPingOnce(completion:(ping:Object)=>{}) {
    if (Platform.OS === 'ios') {
      RNNetworkConnectivityManager.pingTSOnceWithCallback((ping: unknown) => {
        this._handleOnePing(ping);
        completion(ping);
      });
    } else if (Platform.OS === 'android') {
      SkillzModule.pingTSOnceWithCallback((ping: unknown) => {
        this._handleOnePing(ping);
        completion(ping);
      });
    }
  }

  _handleOnePing(ping:Object) {
    this.isWifi = ping.isWifi;
    this.mostRecentPing = ping.ping;
    console.debug('Ping: ' + ping.ping);
  }

  //returns the number of bars to be filled for wifi
  _getWifiSignalStrength(average?: number | null): number {
    const pingToUse = typeof average === 'number' ? average : this.mostRecentPing;
    const minPingTime = ClientPropertyConstants.getSyncMinPingStrength();
    const strongPingTime = ClientPropertyConstants.getSyncStrongPingStrength();
    if (pingToUse > minPingTime) {
      return 2;
    } else if (pingToUse < strongPingTime) {
      return 4;
    } else {
      return 3;
    }
  }

  //returns the number of bubbles to be filled for cell
  _getCellSignalStrength(average?: number | null): number {
    const pingToUse = typeof average === 'number' ? average : this.mostRecentPing;
    const minPingTime = ClientPropertyConstants.getSyncMinPingStrength();
    const strongPingTime = ClientPropertyConstants.getSyncStrongPingStrength();
    if (pingToUse > minPingTime) {
      return 1;
    } else if (pingToUse < strongPingTime) {
      return 5;
    } else {
      return 2;
    }
  }

  _displayLiveMatchWarningModal(tournament: Tournament, navigator: Navigator, completion: ((success: any) => void) | null) {
    const warningIcon = 'ic_warning_exclamation.png';
    const warningTitle = _m("YOU'RE ABOUT TO PLAY A LIVE MATCH");
    const warningMessage = _m('Disconnecting once the match is in progress will forfeit your entry fee.');
    const warningButtonTitles = [_m('Cancel'), _m('Play')];
    const warningModal = StaticWarningModal.createModal(warningIcon,
      warningTitle,
      warningMessage,
      warningButtonTitles,
      (index: unknown) => { this._handleLiveMatchPlayCTAButtons(tournament, index, navigator, completion); });

    const warningModalProps = {
      animationType: 'fade',
      transparent: true,
    };

    ModalNavigationController.showModal(warningModal, warningModalProps);
  }

  _displayWifiSignalWarningModal(tournament: Tournament, wifiStrength: number, navigator: Navigator, completion: (success: any) => void) {
    let signalIcon = '';
    let modalTitle = '';
    let modalMessage = '';
    const wifiSignalIcon2 = 'ic_wifi_signal_2.png';
    const wifiSignalIcon3 = 'ic_wifi_signal_3.png';
    const modalButtonTitles = [_m('Cancel'), _m('Retry')];

    if (wifiStrength < 3) {
      signalIcon = wifiSignalIcon2;
      modalTitle = _m('MATCH UNAVAILABLE');
      modalMessage = _m('Your connection strength is too weak to play this match.');
    } else if (wifiStrength == 3) {
      signalIcon = wifiSignalIcon3;
      modalTitle = _m("YOU'RE ABOUT TO PLAY A LIVE MATCH");
      modalMessage = _m('Your connection strength is lower than recommended. If you disconnect during the match, you will forfeit your entry fee. Do you want to continue?');
      modalButtonTitles[1] = _m('Play');
    }

    const wifiModal = StaticWarningModal.createModal(signalIcon,
      modalTitle,
      modalMessage,
      modalButtonTitles,
      (index: unknown) => {
        this._handlePlayCTAButtons(tournament, index, navigator, wifiStrength === 3, completion);
      },
      () => {
        this._onDismiss(completion);
      });

    const wifiModalProps = {
      animationType: 'fade',
      transparent: true,
    };

    ModalNavigationController.showModal(wifiModal, wifiModalProps);
  }

  _displayCellSignalWarningModal(tournament: Tournament, cellStrength: number, navigator: Navigator, completion: (success: any) => void) {
    const allowCellConnection = ClientPropertyConstants.getIsSyncCellEnabled();
    let signalIcon = '';
    let modalTitle = '';
    let modalMessage = '';
    let isCell = true;
    const cellSignalIcon1 = 'ic_cell_signal_1.png';
    const cellSignalIcon2 = 'ic_cell_signal_2.png';
    const wifiSignalIcon2 = 'ic_wifi_signal_2.png';
    const modalButtonTitles = [_m('Cancel'), _m('Retry')];

    if (cellStrength < 2) {
      signalIcon = allowCellConnection ? cellSignalIcon1 : wifiSignalIcon2;
      isCell = allowCellConnection;
      modalTitle = _m('MATCH UNAVAILABLE');
      modalMessage = allowCellConnection ? _m('Your connection strength is too weak to play this match. Please connect to wifi or find a stronger signal.') :
        _m('This match is not currently supported over cellular. Please connect to wifi and retry.');
    } else if (cellStrength == 2) {
      signalIcon = cellSignalIcon2;
      modalTitle = _m("YOU'RE ABOUT TO PLAY A LIVE MATCH");
      modalMessage = _m('Your connection strength is lower than recommended. If you disconnect during the match, you will forfeit your entry fee. Do you want to continue?');
      modalButtonTitles[1] = _m('Play');
    }

    const cellModal = (
      <StaticWarningModal icon={signalIcon}
                          title={modalTitle}
                          message={modalMessage}
                          buttonTitleArray={modalButtonTitles}
                          callback={(index: unknown) => {
                            this._handlePlayCTAButtons(tournament, index, navigator, cellStrength === 2, completion);
                          }}
                          onDismiss={() => {
                            this._onDismiss(completion);
                          }}
                          isCell={isCell}/>
    );

    const cellModalProps = {
      animationType: 'fade',
      transparent: true,
    };
    ModalNavigationController.showModal(cellModal, cellModalProps);
  }

  _onDismiss(completion:(success:boolean) => void) {
    completion(false);
  }

  _handleLiveMatchPlayCTAButtons(tournament: Tournament, index: number, navigator: Navigator, completion: (success: boolean) => void) {
    ModalNavigationController.hideModal();
    if (index === 0) {
      completion(false);
    } else if (index === 1) {
      completion(true);
    }
  }

  _handlePlayCTAButtons(tournament: Tournament, index: number, navigator: Navigator, isPlay: boolean, completion: (success: boolean) => void) {
    if (index === 0) {
      ModalNavigationController.hideModal();
      completion(false);
    } else if (index === 1) {
      ModalNavigationController.hideModal();
      if (isPlay) {
        completion(true);
      } else {
        this.checkConnection(tournament, navigator, completion, true);
      }
    }
  }

  _hideLoading() {
    //This is delayed because the loading indicator and the modal can't be modified simultaneously.
    setTimeout(() => {
      LoadingIndicator.hide();
    }, 300);
  }
}

const NetworkConnectivityClientInstance = new NetworkConnectivityClient();
export default NetworkConnectivityClientInstance;
